import React, { useMemo } from 'react'
import { formatNumber } from 'utils/formatters'
import { Cell, Label, Pie, PieChart, ResponsiveContainer, PieLabelRenderProps } from 'recharts'
import { useTheme } from '@mui/material/styles'
import { PieChartData } from 'utils/types'
import { NOT_AVAILABLE } from 'utils/constants'
import styles from './Common.scss'

const RADIAN = Math.PI / 180

interface CustomLabelProps extends PieLabelRenderProps {
  value: number
}

interface GenericPieChartProps {
  chartData: PieChartData[]
  isAnimationActive?: boolean
}

const GenericPieChart: React.FC<GenericPieChartProps> = ({ chartData, isAnimationActive = false }) => {
  const theme = useTheme()

  const renderCustomizedLabel = (props: CustomLabelProps) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, value } = props
    if (!cx || !cy || !midAngle || !innerRadius || !outerRadius) return null

    const cxNum = Number(cx)
    const cyNum = Number(cy)
    const midAngleNum = Number(midAngle)
    const innerRadiusNum = Number(innerRadius)
    const outerRadiusNum = Number(outerRadius)

    const radius = innerRadiusNum + (outerRadiusNum - innerRadiusNum) * 0.5
    const x = cxNum + radius * Math.cos(-midAngleNum * RADIAN)
    const y = cyNum + radius * Math.sin(-midAngleNum * RADIAN)
    const formattedValue = formatNumber(value)
    return (
      <text
        x={x}
        y={y}
        fill='white'
        textAnchor='middle'
        dominantBaseline='central'
        fontFamily='Source Sans Pro'
        className={styles.textOnChart}
      >
        {formattedValue === NOT_AVAILABLE ? '' : formattedValue}
      </text>
    )
  }

  const totalValue = useMemo(() => chartData.reduce((acc, cur) => acc + cur.value, 0), [chartData])

  const getChartColor = (name: string): string => {
    const chartsTheme = theme.palette?.chartsTheme as Record<string, string> | undefined
    const color = chartsTheme?.[name.toLowerCase()]
    return color || theme.palette?.primary?.main || '#000000'
  }

  return (
    <ResponsiveContainer width='100%' aspect={1}>
      <PieChart>
        <Pie
          data={chartData}
          cx='50%'
          cy='50%'
          labelLine={false}
          minAngle={40}
          label={renderCustomizedLabel as any}
          outerRadius='100%'
          innerRadius='40%'
          dataKey='value'
          isAnimationActive={isAnimationActive}
        >
          <Label
            value={formatNumber(totalValue)}
            position='center'
            fill='#000'
            fontWeight={600}
            className={styles.labelOnChart}
          />
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={getChartColor(entry.name)} />
          ))}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  )
}

export default GenericPieChart
